import * as React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import {fetchPrCampaignEdit, upsertPrCampaign} from "./portalRedirectCampaignsService";
import PrConfirmDialog from "./prConfirmDialog";
import Loading from "../../components/loading";

const TIERS = ["A", "B", "C", "D", "E", "F", "G"] as const;

function cleanTiers(t: any) {
    const src = t || {};
    return {
        A: !!src.A,
        B: !!src.B,
        C: !!src.C,
        D: !!src.D,
        E: !!src.E,
        F: !!src.F,
        G: !!src.G,
    };
}

type Props = {
    row: any | null | "create";
    onClose: () => void;
    onSaved: () => void;
};

export default function PrUpsertModal({row, onClose, onSaved}: Props) {
    const create = row === "create";
    const open = row != null;
    const [form, setForm] = React.useState<any>(null);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            setForm(null);
            setConfirmOpen(false);
            setSaving(false);
            setLoading(false);
            return;
        }
        if (create) {
            setForm({
                hpCampaignId: "",
                hpCampaignIdLb: "",
                landingPage: "",
                dailyCap: 0,
                monthlyCap: 0,
                redirectType: "Hosted",
                ppi: 0,
                dailyLbMatchPercent: 0,
                activeFlag: true,
                isBranded: false,
                comment: "",
                tiers: {A: true, B: true, C: true, D: true, E: true, F: false, G: false},
            });
            return;
        }
        let cancelled = false;
        setLoading(true);
        setForm(null);
        (async () => {
            const edit = await fetchPrCampaignEdit(row.id);
            if (cancelled) {
                return;
            }
            if (!edit) {
                toast.error("Failed to load campaign edit fields.");
                setLoading(false);
                onClose();
                return;
            }
            setForm({
                portalRedirectId: row.id,
                hpCampaignId: row.hpCampaignId,
                hpCampaignIdLb: row.hpCampaignIdLb || "",
                landingPage: edit.landingPage || "",
                dailyCap: row.dailyCap,
                monthlyCap: row.monthlyCap,
                redirectType: row.redirectType || "Hosted",
                ppi: row.ppi || 0,
                dailyLbMatchPercent: row.dailyLbMatchPercent || 0,
                activeFlag: !!row.activeFlag,
                isBranded: !!row.isBranded,
                comment: "",
                tiers: cleanTiers(edit.tiers),
            });
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, create, row, onClose]);

    if (!open) {
        return null;
    }

    if (loading || !form) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>{create ? "Create Campaign" : "Update Campaign"}</DialogTitle>
                <DialogContent><Loading/></DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                </DialogActions>
            </Dialog>
        );
    }

    const set = (k: string, v: any) => setForm({...form, [k]: v});

    const save = async () => {
        setSaving(true);
        const lb = form.hpCampaignIdLb === "" || form.hpCampaignIdLb == null ? null : Number(form.hpCampaignIdLb);
        const ok = await upsertPrCampaign({
            portalRedirectId: create ? undefined : Number(form.portalRedirectId),
            hpCampaignId: Number(form.hpCampaignId),
            hpCampaignIdLb: lb,
            landingPage: form.landingPage,
            dailyCap: Number(form.dailyCap),
            monthlyCap: Number(form.monthlyCap),
            redirectType: form.redirectType,
            ppi: Number(form.ppi),
            dailyLbMatchPercent: form.dailyLbMatchPercent === "" ? null : Number(form.dailyLbMatchPercent),
            activeFlag: !!form.activeFlag,
            isBranded: !!form.isBranded,
            tiers: cleanTiers(form.tiers),
            comment: form.comment || null,
        });
        if (ok) {
            toast.success(create ? `${form.hpCampaignId} created successfully.` : `${row.schoolName} - ${row.appId} updated successfully.`);
            setConfirmOpen(false);
            onSaved();
        }
        setSaving(false);
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{create ? "Create Campaign" : "Update Campaign"}</DialogTitle>
            <DialogContent>
                {!create && row && <Typography sx={{mb: 2}}><b>{row.brokerName} &gt; {row.schoolName} - {row.appId}</b></Typography>}
                <Box sx={{display: "flex", gap: 1, mb: 1}}>
                    <TextField fullWidth label="Campaign Id" type="number" value={form.hpCampaignId} onChange={(e) => set("hpCampaignId", e.target.value)}/>
                    <TextField fullWidth label="LB Campaign Id" type="number" value={form.hpCampaignIdLb} onChange={(e) => set("hpCampaignIdLb", e.target.value)}/>
                </Box>
                <TextField fullWidth label="Landing Page" value={form.landingPage} onChange={(e) => set("landingPage", e.target.value)} sx={{mb: 0.5}}/>
                <Typography variant="caption" sx={{display: "block", mb: 1}}>
                    Link Out Tokens: <b>%z%</b> zip, <b>%publisher_id%</b> pub, <b>%lead_id%</b> lead id, <b>%kw%</b> aoi, <b>%e%</b> edu level, <b>%d%</b> doi
                </Typography>
                <Box sx={{display: "flex", gap: 1, mb: 1}}>
                    <TextField fullWidth label="Daily Cap" type="number" value={form.dailyCap} onChange={(e) => set("dailyCap", e.target.value)}/>
                    <TextField fullWidth label="Monthly Cap" type="number" value={form.monthlyCap} onChange={(e) => set("monthlyCap", e.target.value)}/>
                </Box>
                <Box sx={{display: "flex", gap: 1, mb: 1}}>
                    <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select label="Type" value={form.redirectType} onChange={(e) => set("redirectType", e.target.value)}>
                            <MenuItem value="Hosted">Hosted</MenuItem>
                            <MenuItem value="Linked">Linked</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField fullWidth label="PPI" type="number" value={form.ppi} onChange={(e) => set("ppi", e.target.value)}/>
                </Box>
                <TextField fullWidth label="Max Daily LB Match Percent" type="number" value={form.dailyLbMatchPercent} onChange={(e) => set("dailyLbMatchPercent", e.target.value)} helperText="0 or blank = unlimited LB" sx={{mb: 1}}/>
                <Box sx={{display: "flex", gap: 3, mb: 1}}>
                    <FormControlLabel control={<Switch checked={!!form.activeFlag} onChange={(e) => set("activeFlag", e.target.checked)}/>} label="Active"/>
                    <FormControlLabel control={<Switch checked={!!form.isBranded} onChange={(e) => set("isBranded", e.target.checked)}/>} label="Branded"/>
                </Box>
                <Typography variant="subtitle2">Weights</Typography>
                {TIERS.map((t) => (
                    <FormControlLabel key={t} control={<Switch checked={!!form.tiers[t]} onChange={(e) => set("tiers", {...cleanTiers(form.tiers), [t]: e.target.checked})}/>} label={`Tier ${t}`}/>
                ))}
                <TextField fullWidth multiline minRows={4} label="New Comment" value={form.comment} onChange={(e) => set("comment", e.target.value)} sx={{mt: 1}}/>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => setConfirmOpen(true)}>{create ? "Create" : "Save"}</Button>
            </DialogActions>
        </Dialog>
            <PrConfirmDialog
                open={confirmOpen}
                message={create ? "Are you sure you want to create this campaign?" : "Are you sure you want to save campaign changes?"}
                loading={saving}
                onClose={() => { if (!saving) setConfirmOpen(false); }}
                onConfirm={save}
            />
        </>
    );
}
